import { useState } from "react";
import { Link, NavLink as RRNavLink, useHistory, useLocation } from "react-router-dom";
import {
  Collapse,
  Container,
  Nav,
  NavItem,
  NavLink,
  NavbarBrand,
  NavbarToggler,
  Navbar as ReactstrapNavbar,
} from "reactstrap";
import styled from "styled-components";
import logo from "../../assets/img/svg/listino-logo-horizontal-primary.svg";

import { isSellerRoute } from "../../contexts/AccountContext";
import { getToken, signOutEverywhere } from "../../utils/auth";
import { BalanceChip } from "../Credits";

const StyledNavbar = styled(ReactstrapNavbar)`
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: saturate(140%) blur(10px);
  -webkit-backdrop-filter: saturate(140%) blur(10px);
  border-bottom: 1px solid var(--brand-border);
  box-shadow: var(--brand-shadow-sm);
  padding: 10px 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
`;

const LogoContainer = styled(NavbarBrand)`
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--brand-ink) !important;
  font-family: var(--brand-font-serif);
  font-weight: 600;
  font-size: 18px;
  letter-spacing: -0.02em;

  &:hover {
    color: var(--brand-primary-700) !important;
  }
`;

const BrandMark = styled.img`
  width: 110px;
  height: auto;
  display: block;
  flex-shrink: 0;
`;

const NavStyled = styled(Nav)`
  align-items: center;
  gap: 4px;

  @media (max-width: 767.98px) {
    align-items: flex-start;
    width: 100%;
  }
`;

const StyledNavLink = styled(NavLink)`
  color: var(--brand-ink-2) !important;
  font-weight: 500;
  font-size: 14px;
  padding: 8px 14px !important;
  border-radius: 8px;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: var(--brand-ink) !important;
    background: var(--brand-bg-soft);
  }

  &.active {
    color: var(--brand-primary-700) !important;
    font-weight: 600;
    background: var(--brand-primary-50);
  }
`;

const CTAButton = styled(NavLink)`
  background: var(--brand-primary) !important;
  color: #fff !important;
  font-weight: 600;
  font-size: 14px;
  padding: 11px 20px !important;
  border-radius: var(--brand-radius);
  box-shadow: var(--brand-shadow-primary);
  transition: background 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;
  border: none;
  margin-left: 8px;

  &:hover {
    background: var(--brand-primary-600) !important;
    transform: translateY(-1px);
    box-shadow: var(--brand-shadow-primary-hover);
  }
`;

const SignOutButton = styled.button`
  color: var(--brand-ink-2);
  font-weight: 500;
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 8px;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: var(--brand-ink);
    background: var(--brand-bg-soft);
  }
`;

// The balance sits OUTSIDE the mobile collapse so it is always one glance
// away (CREDITS_UI_MAP rule 1): after the brand on phones, at the far right
// on desktop. Never on seller-visible chrome (rule 6).
const ChipSlot = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-right: 12px;

  @media (min-width: 768px) {
    order: 3;
    margin-left: 8px;
    margin-right: 0;
  }
`;

const NewNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  // Auth state is read at render (the codebase's non-reactive token model);
  // every login/logout navigates, which remounts the navbar.
  const authed = !!getToken();
  const showChip = authed && !isSellerRoute(location.pathname, location.search);

  const toggle = () => setIsOpen(!isOpen);
  const signOut = () => {
    signOutEverywhere();
    history.push("/");
  };

  return (
    <StyledNavbar light expand="md">
      <Container>
        <LogoContainer tag={Link} to="/">
          <BrandMark src={logo} alt="Listino logo" />
        </LogoContainer>
        {showChip && (
          <ChipSlot>
            <BalanceChip />
          </ChipSlot>
        )}
        <NavbarToggler onClick={toggle} />
        <Collapse isOpen={isOpen} navbar>
          <NavStyled className="ml-auto" navbar>
            <NavItem>
              <StyledNavLink to="/pricing" tag={RRNavLink} activeClassName="active">
                Pricing
              </StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/about" tag={RRNavLink} activeClassName="active">
                About Us
              </StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/resources" tag={RRNavLink} activeClassName="active">
                Resources
              </StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/faq" tag={RRNavLink} activeClassName="active">
                FAQ
              </StyledNavLink>
            </NavItem>
            {authed ? (
              <>
                <NavItem>
                  <StyledNavLink to="/dashboard" tag={RRNavLink} activeClassName="active">
                    Your listings
                  </StyledNavLink>
                </NavItem>
                <NavItem>
                  <StyledNavLink to="/account" tag={RRNavLink} activeClassName="active">
                    Account
                  </StyledNavLink>
                </NavItem>
                <NavItem>
                  <StyledNavLink to="/create-video" tag={RRNavLink} activeClassName="active">
                    Create video
                  </StyledNavLink>
                </NavItem>
                <NavItem>
                  <SignOutButton type="button" onClick={signOut}>
                    Sign out
                  </SignOutButton>
                </NavItem>
              </>
            ) : (
              <>
                <NavItem>
                  <StyledNavLink to="/login" tag={RRNavLink} activeClassName="active">
                    Sign in
                  </StyledNavLink>
                </NavItem>
                <NavItem>
                  <CTAButton to="/verify" tag={RRNavLink}>
                    Start free
                  </CTAButton>
                </NavItem>
              </>
            )}
          </NavStyled>
        </Collapse>
      </Container>
    </StyledNavbar>
  );
};

export default NewNavbar;
